import { supabase } from './supabase';
import { type UserProfile } from '../types';
import { requestNotificationPermission } from './iosBridge';

// Generate a random string for PKCE
function generateRandomString(length: number): string {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let text = '';
  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
}

// Store code verifier in localStorage
function storeCodeVerifier(codeVerifier: string): void {
  localStorage.setItem('pkce_code_verifier', codeVerifier);
}

// Get code verifier from localStorage
function getCodeVerifier(): string | null {
  return localStorage.getItem('pkce_code_verifier');
}

// Clear code verifier from localStorage
function clearCodeVerifier(): void {
  localStorage.removeItem('pkce_code_verifier');
}

// Helper function to clean up stale sessions
const cleanupStaleSessions = async () => {
  try {
    // Clear any stale sessions from localStorage
    const authKeys = Object.keys(localStorage).filter(key => 
      (key.startsWith('sb-') && key.includes('auth-token')) || 
      key.startsWith('supabase.auth.token')
    );
    
    for (const key of authKeys) {
      try {
        const value = localStorage.getItem(key);
        if (!value) {
          localStorage.removeItem(key);
          continue;
        }
        
        // Try to parse token data
        const tokenData = JSON.parse(value);
        
        // Check if token is expired
        if (tokenData.expiresAt && tokenData.expiresAt < Date.now() / 1000) {
          console.log('Removing expired token', key);
          localStorage.removeItem(key);
        }
      } catch (e) {
        // If we can't parse the token data, it might be corrupted
        console.warn('Removing potentially corrupted token', key);
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.warn('Error cleaning up sessions:', error);
  }
};

// Google OAuth sign in
export async function signInWithGoogle(): Promise<UserProfile | null> {
  await cleanupStaleSessions();

  try {
    // Check if we're already handling a redirect
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');

    if (code) {
      console.log('OAuth code detected in signInWithGoogle, skipping new flow');
      return null;
    }

    // If no session, initiate OAuth flow
    const codeVerifier = generateRandomString(64);
    storeCodeVerifier(codeVerifier);

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent'
        },
        skipBrowserRedirect: false,
        flowType: 'pkce',
        codeVerifier
      }
    });

    if (error) {
      console.error('Error signing in with Google:', error.message);
      throw new Error('Failed to sign in with Google. Please try again later.');
    }

    return null; // Return null as the OAuth flow will redirect
  } catch (error) {
    console.error('Error initiating Google sign in:', error);
    throw new Error('Failed to sign in with Google. Please try again later.');
  }
}

// Helper function for exponential backoff delay
const getExponentialBackoffDelay = (attempt: number, baseDelay: number = 1000): number => {
  return Math.min(baseDelay * Math.pow(2, attempt - 1), 10000); // Cap at 10 seconds
};

// Email sign up with improved error handling and retries
export async function signUpWithEmail(email: string, password: string): Promise<UserProfile | null> {
  await cleanupStaleSessions();
  
  const maxRetries = 3;
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Try to sign up directly - Supabase will handle duplicate users
      const { data: { user, session }, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (signUpError) {
        if (signUpError.message.includes('User already registered')) {
          throw new Error('An account with this email already exists. Please sign in instead.');
        }
        throw signUpError;
      }

      if (!user || !session) {
        throw new Error('Failed to create account. Please try again.');
      }

      requestNotificationPermission();

      const isUserAdmin = ['alinickmaleki@gmail.com', 'alinickmaleki@ymail.com'].includes(email);

      return {
        uid: user.id,
        email: user.email || '',
        targetLanguage: 'Dutch',
        nativeLanguage: 'English',
        currentLevel: 'A1',
        lessonsCompleted: [],
        dateJoined: new Date(),
        isAdmin: isUserAdmin,
        hasSeenGuide: false
      };
    } catch (err) {
      lastError = err instanceof Error ? err : new Error('An unexpected error occurred');
      
      if (attempt < maxRetries && err instanceof Error && 
         (err.message.includes('temporarily unavailable') || err.message.includes('Database error'))) {
        const delay = getExponentialBackoffDelay(attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      throw lastError;
    }
  }

  throw lastError || new Error('Unable to create account at this time. Please try again later.');
}

// Email sign in with improved error handling
export async function signInWithEmail(email: string, password: string): Promise<UserProfile | null> {
  await cleanupStaleSessions();
  
  try {
    const { data: { session }, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        throw new Error('Invalid email or password. Please check your credentials and try again.');
      }
      throw error;
    }

    if (!session?.user) {
      throw new Error('Failed to sign in. Please try again.');
    }

    requestNotificationPermission();

    return {
      uid: session.user.id,
      email: session.user.email || '',
      targetLanguage: 'Dutch',
      nativeLanguage: 'English',
      currentLevel: 'A1',
      lessonsCompleted: [],
      dateJoined: new Date(),
      isAdmin: ['alinickmaleki@gmail.com', 'alinickmaleki@ymail.com'].includes(email),
      hasSeenGuide: false
    };
  } catch (err) {
    if (err instanceof Error) {
      throw err;
    }
    throw new Error('An unexpected error occurred. Please try again later.');
  }
}

export async function signOut() {
  try {
    await cleanupStaleSessions();
    
    // First clear any local storage data before calling signOut to prevent potential race conditions
    // Only keep essential items that might be needed for re-login experience
    const keepItems = ['hasSeenGuide', 'currentLevel'];
    const toKeep: Record<string, string> = {};
    
    keepItems.forEach(item => {
      const value = localStorage.getItem(item);
      if (value) toKeep[item] = value;
    });
    
    localStorage.clear();
    
    // Restore essential items
    Object.entries(toKeep).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
    
    clearCodeVerifier();
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error.message);
      throw new Error('Unable to sign out at this time. Please try again later.');
    }
  } catch (err) {
    console.error('Error during sign out:', err);
    throw new Error('Unable to sign out at this time. Please try again later.');
  }
}

// Function to handle OAuth callback
export async function handleOAuthCallback(code: string): Promise<UserProfile | null> {
  try {
    const codeVerifier = getCodeVerifier();
    
    if (!codeVerifier) {
      console.error('No code verifier found in localStorage');
      throw new Error('Authentication failed. Please try again.');
    }
    
    const { data, error } = await supabase.auth.exchangeCodeForSession(code);
    
    if (error) {
      console.error('Error exchanging code for session:', error);
      throw error;
    }
    
    if (!data?.session?.user) {
      throw new Error('Failed to authenticate. Please try again.');
    }
    
    clearCodeVerifier();

    requestNotificationPermission();

    const isAdmin = ['alinickmaleki@gmail.com', 'alinickmaleki@ymail.com'].includes(data.session.user.email || '');

    return {
      uid: data.session.user.id,
      email: data.session.user.email || '',
      targetLanguage: localStorage.getItem('targetLanguage') || 'Dutch',
      nativeLanguage: localStorage.getItem('nativeLanguage') || 'English',
      currentLevel: localStorage.getItem('currentLevel') || 'A1',
      lessonsCompleted: [],
      dateJoined: new Date(),
      isAdmin,
      hasSeenGuide: localStorage.getItem('hasSeenGuide') === 'true'
    };
  } catch (error) {
    clearCodeVerifier();
    console.error('Error handling OAuth callback:', error);
    throw error;
  }
}